import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';
import styled, { createGlobalStyle } from 'styled-components';
import { InfoCircle } from 'components/shared/InfoCircle';
import {
  Input,
  TextField as MUITextField,
  FormControl,
  InputLabel,
} from '@mui/material';

const TextMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{
        '#': /[1-9]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

TextMaskCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const MaskedInputComponent = React.forwardRef(function MaskedInputComponent(
  props,
  ref
) {
  return (
    <Input
      value={props.value}
      onChange={props.onChange}
      onBlur={props.onBlur}
      name={props.name}
      label={props.label}
      ref={ref}
      id="formatted-text-mask-input"
      inputComponent={TextMaskCustom}
    />
  );
});

const StyledLabel = styled(InputLabel)`
  &&&&&& {
    color: #000;
    font-family: 'Whitney Medium';
    left: -14px;
    position: absolute;
    top: -14px;

    &.disabled {
      color: #888888;
    }
  }
`;

const LabelContainer = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  height: 0;
`;

const StyledFormControl = styled(FormControl)`
  min-width: 100%;

  .MuiOutlinedInput-notchedOutline,
  .MuiInputBase-root {
    border-color: #888888;
    border-width: 1px;
    border-radius: 0;
    font-family: 'Whitney Book';
    width: 100%;

    &.Mui-focused,
    &:active,
    &:focus,
    &:target,
    &:hover {
      border-color: #006c9c;
      border-width: 2px;
    }
  }

  .outlined-input {
    &:active,
    &:focus,
    &:target,
    &:hover {
      border-color: #006c9c;
      border-width: 2px;
    }
  }

  &&&&&&&&&& .Mui-error {
    .MuiOutlinedInput-notchedOutline {
      border-color: #bf1e2d;
    }
  }

  &:focus-visible {
    outline: none;
  }

  &.without-label {
    .MuiTextField-root {
      margin-top: 0;
    }
  }

  .MuiTextField-root {
    margin-top: 32px;
    margin-bottom: 0;
  }

  .MuiOutlinedInput-root {
    input[type='text'],
    input[type='number'] {
      border: 0;
      margin-bottom: 8px;
      outline: none;
      padding-left: 10px;
      padding-top: 8px;
      &:focus {
        box-shadow: none;
        outline: none;
        border: 0;
      }
    }
  }

  .helper-text {
    color: #555;
    font-size: 90%;
    margin-top: 0.25rem;
  }

  .MuiFormHelperText-root.Mui-error {
    color: #bf1e2d;
    font-family: Whitney Medium;
    font-size: 13px;
    margin-left: 0;
  }

  .Mui-disabled {
    .MuiOutlinedInput-notchedOutline {
      border: 1px solid #a6a6a6 !important;
      color: #888888;
    }
  }
  .MuiInput-root:before {
    border-bottom: none;
  }
  .MuiInput-root:after {
    border-bottom: none;
  }
  .MuiInput-root:hover:not(.Mui-disabled):before {
    border-bottom: none;
  }
`;

export function TextField({
  autoFocus,
  value,
  label,
  variant,
  multiline,
  placeholder,
  id,
  inputProps,
  required,
  name,
  phoneNumberMask,
  disabled,
  errorMessage,
  error,
  type,
  infoText,
  helperText,
  inputRef,
  rows,
  onBlur = () => {},
  onChange = () => {},
  onMouseOut = () => {},
  onClick = () => {},
}) {
  const [labelWidth, setLabelWidth] = useState();

  const labelRef = useRef();

  useEffect(() => {
    if (labelRef.current) {
      setLabelWidth(labelRef.current.offsetWidth);
    }
  }, []);

  return (
    <StyledFormControl
      error={error}
      variant="outlined"
      className={!label && 'without-label'}
      onClick={onClick}
    >
      {label && (
        <LabelContainer>
          <div className="label-div" style={{ width: labelWidth }}>
            <StyledLabel
              htmlFor={id}
              ref={labelRef}
              className={disabled && 'disabled'}
            >
              {label}
              {!required && ' (optional)'}
            </StyledLabel>
          </div>
          {infoText && <InfoCircle clickable text={infoText} />}
        </LabelContainer>
      )}
      <MUITextField
        autoFocus={autoFocus}
        margin="normal"
        onChange={(event) => {
          onChange(event);
        }}
        value={value}
        id={id}
        name={name}
        required={required}
        variant={variant}
        inputProps={{
          classes: { select: 'outlined-select' },
          type: type || 'text',
          onBlur: onBlur,
          onChange: onChange,
          onMouseOut: () => onMouseOut(),
        }}
        multiline={multiline}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={errorMessage}
        rows={rows}
        inputRef={inputRef}
        InputProps={
          multiline
            ? inputProps
            : {
                inputComponent: phoneNumberMask ? MaskedInputComponent : null,
                ...inputProps,
              }
        }
      />
      {helperText && <div className="helper-text">{helperText}</div>}
    </StyledFormControl>
  );
}
